import store from '@/store';
import products from '@/components/products';
import i18n from './i18n';
import api from '@/api';
import log from '@/configured/logger';
import { LANGUAGE_CONFIG } from '@/configured/languages/config';

const exports = {
  namespace: 'i18n',
  module: {
    namespaced: true,
    state: {
      language: 'en',
      availableLanguages: [],
      loadedLanguages: [],
    },
    mutations: {
      ADD_LANGUAGE,
      SET_LANGUAGE: (state, lang) => (state.language = lang),
      SET_AVAILABLE_LANGUAGES: (state, languages) =>
        (state.availableLanguages = languages),
    },
    actions: {
      addLanguage: ({ commit }, lang, messages) => {
        commit('ADD_LANGUAGE', lang, messages);
      },
      setLanguage: ({ commit, state }, lang) => {
        const normalized = normalizeLanguage(lang, state.language);
        if (normalized !== lang) {
          log.warn(`Normalizing language in setLanguage: "${lang}" -> "${normalized}"`);
        }
        commit('SET_LANGUAGE', normalized);
      },
      loadLanguage,
      reloadLanguageMessages,
      getAvailableLanguages,
    },
  },
};

store.registerModule(exports.namespace.split('/'), exports.module);

export default exports;

//////////

function ADD_LANGUAGE(state, { language, messages }) {
  state[language] = messages;
  state.loadedLanguages.push(language);
}

async function loadLanguage({ state, commit }, language) {
  language = normalizeLanguage(language, state.language);

  // If the language changed, clear/reload cached data that contains translated copy.
  const needsLanguageUpdate = language !== state.language;
  if (needsLanguageUpdate) {
    await store.dispatch(`${products.store.namespace}/clearCachedProducts`);
    // Expose target locale immediately so route guards / GraphQL site match session UI before
    // translation HTTP completes (login dropdown vs stale preferredLanguage on the account).
    commit('SET_LANGUAGE', language);
  }

  const request = api.get(`/api/${language}/translations`).then((rsp) => {
    const messages = rsp && rsp.data && rsp.data.data ? rsp.data.data : rsp.data;

    if (JSON.stringify(state[language]) !== JSON.stringify(messages)) {
      commit('ADD_LANGUAGE', { language, messages });
      i18n.setLocaleMessage(language, messages);
    }

    return messages;
  });

  if (state.loadedLanguages.includes(language)) {
    // If this is already loaded, return immediately and
    // let the request run in the background.
    log.info('Language is already loaded. Resolving immediately', language);
    return Promise.resolve(state[language]);
  } else {
    // Otherwise return a promise to resolve with the data
    log.info('Loading language', language);
    return request;
  }
}

function normalizeLanguage(language, currentLanguage = 'en') {
  if (!language) {
    return currentLanguage || 'en';
  }

  if (LANGUAGE_CONFIG[language]) {
    return language;
  }

  if (String(language).startsWith('site')) {
    return currentLanguage || 'en';
  }

  return 'en';
}

function reloadLanguageMessages({ state }) {
  state.loadedLanguages.forEach((lang) => {
    i18n.setLocaleMessage(lang, state[lang]);
  });
}

function getAvailableLanguages({ state, commit }) {
  return api.get(`/api/sites`).then((response) => {
    const sites = response && response.data && response.data.data ? response.data.data : response.data;
    commit('SET_AVAILABLE_LANGUAGES', sites);
  });
}
